/**
 * The sidebar quick search: Enter in the sidebar search box opens the travel
 * file list with the text placed in ONE of the list's existing filters, picked
 * from what the text looks like. The list then shows it in that filter's own
 * field (Number, Tour Code, Name, ...), as if the user had typed it there.
 */

/** The travel file list filters a quick search can fill — their `fieldName`s. */
export const QUICK_SEARCH_KEYS = [
    'refNumber',
    'code1',
    'code2',
    'phone',
    'nationalId',
    'ticketNumber',
    'pnrNumber',
    'name'
] as const;

export type QuickSearchKey = typeof QUICK_SEARCH_KEYS[number];
export type QuickSearchFilter = Partial<Record<QuickSearchKey, string>>;

const TICKET_NUMBER_LENGTH = 13;
const NATIONAL_ID_LENGTH = 9;

/**
 * Which filter the typed text belongs in:
 *
 * | Text                                      | Filter            |
 * |-------------------------------------------|-------------------|
 * | `TKK 1129`, `TKK-1129`, `TKK/1129`, `TKK1129` | code1 + code2 |
 * | `+972…` / `972…`                          | phone             |
 * | 13 digits                                 | ticket number     |
 * | 10 digits starting 0, or 9 starting 05    | phone             |
 * | other 9 digits                            | national ID, else TF number (see resolveQuickSearch) |
 * | up to 8 digits                            | TF number         |
 * | 6 letters and digits, mixed               | PNR               |
 * | anything else                             | passenger name    |
 *
 * Letters alone (`TKK`) are a name: a tour code needs its number.
 * Returns null for blank text.
 */
export const classifyQuickSearch = (text: string): QuickSearchFilter | null => {
    const term = text.trim();
    if (!term) return null;

    const tourCode = term.match(/^([A-Za-z]+)[\s\-/]*(\d+)$/);
    if (tourCode) return { code1: tourCode[1].toUpperCase(), code2: tourCode[2] };

    // Digits, allowing the separators people type in phone numbers.
    const compact = term.replace(/[\s\-().]/g, '');
    if (/^\+?\d+$/.test(compact)) {
        const digits = compact.replace(/^\+/, '');
        if (compact.startsWith('+') || digits.startsWith('972')) return { phone: term };
        if (digits.length === TICKET_NUMBER_LENGTH) return { ticketNumber: digits };
        if (digits.length === 10 && digits.startsWith('0')) return { phone: term };
        if (digits.length === NATIONAL_ID_LENGTH) {
            return digits.startsWith('05') ? { phone: term } : { nationalId: digits };
        }
        // Up to 8 digits is a TF number; an odd longer number
        // still goes to Number, the most likely meaning of a bare number.
        return { refNumber: digits };
    }

    if (/^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9]{6}$/.test(term)) return { pnrNumber: term.toUpperCase() };

    return { name: term };
};

/**
 * A 9-digit number is read as a national ID first. When no travel file has a
 * passenger with that ID, it is tried as a travel file number instead — which
 * is how the temporary number of an unpaid (`In Process`) file (900,000,000 +
 * id) is found. `countByNationalId` answers how many travel files match; if it
 * fails, the ID search is kept.
 */
export const resolveQuickSearch = async (
    filter: QuickSearchFilter,
    countByNationalId: (nationalId: string) => Promise<number>
): Promise<QuickSearchFilter> => {
    if (!filter.nationalId) return filter;
    try {
        const count = await countByNationalId(filter.nationalId);
        return count > 0 ? filter : { refNumber: filter.nationalId };
    } catch {
        return filter;
    }
};

/** The URL the sidebar opens: the travel file list with the filter in its query string. */
export const quickSearchUrl = (filter: QuickSearchFilter) =>
    `/reservations/tour/travel-file-list?${new URLSearchParams(filter as Record<string, string>).toString()}`;

/** The quick-search filter carried in a URL's query string, or null when there is none. */
export const readQuickSearch = (search: string): QuickSearchFilter | null => {
    const params = new URLSearchParams(search);
    const filter: QuickSearchFilter = {};
    QUICK_SEARCH_KEYS.forEach((key) => {
        const value = params.get(key)?.trim();
        if (value) filter[key] = value;
    });
    return Object.keys(filter).length > 0 ? filter : null;
};
